import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Loader2, User } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile as updateAuthProfile } from 'firebase/auth';
import { db, storage } from '@/lib/firebase';
import { signOutUser } from '@/lib/signOut';
import { useAuth } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Select } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { toast } from '@/components/ui/Toast';
import { BIO_MAX_CHARS, isLikelyStudentNumberLocalPart } from '@makhe/shared';
import { InstitutionBadge } from '@/components/trust/InstitutionBadge';
import { ProfileReelEditor } from '@/components/seller/ProfileReelEditor';
import { compressImage } from '@/lib/image';

const schema = z.object({
  displayName: z.string().min(2).max(60),
  bio: z.string().max(BIO_MAX_CHARS).optional(),
  phoneNumber: z.string().optional(),
  gender: z.enum(['female', 'male', 'non_binary', 'prefer_not_to_say']).optional(),
  residence: z.string().optional(),
  campus: z.string().optional(),
  address: z.string().optional(),
});
type Values = z.infer<typeof schema>;

export default function ProfilePage() {
  const { firebaseUser, userDoc } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [photoURL, setPhotoURL] = useState(userDoc?.photoURL ?? '');
  const { register, handleSubmit, reset, formState: { isSubmitting, errors } } = useForm<Values>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!userDoc) return;
    reset({
      displayName: userDoc.displayName,
      bio: userDoc.bio ?? '',
      phoneNumber: userDoc.phoneNumber ?? '',
      gender: userDoc.gender,
      residence: userDoc.location.residence ?? '',
      campus: userDoc.location.campus ?? '',
      address: userDoc.location.address ?? '',
    });
    setPhotoURL(userDoc.photoURL ?? '');
  }, [userDoc, reset]);

  if (!firebaseUser || !userDoc) return null;

  const onPhotoChange = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Pick an image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5 MB.');
      return;
    }
    setUploading(true);
    try {
      const blob = await compressImage(file, { maxDim: 512 });
      const path = `users/${firebaseUser.uid}/profile/${Date.now()}-${file.name}`;
      const r = ref(storage, path);
      await uploadBytes(r, blob, { contentType: blob.type || 'image/jpeg' });
      const url = await getDownloadURL(r);
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        photoURL: url,
        updatedAt: serverTimestamp(),
      });
      await updateAuthProfile(firebaseUser, { photoURL: url });
      setPhotoURL(url);
      toast.success('Photo updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (values: Values) => {
    try {
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        displayName: values.displayName,
        bio: values.bio ?? null,
        phoneNumber: values.phoneNumber ?? null,
        gender: values.gender ?? null,
        'location.residence': values.residence ?? null,
        'location.campus': values.campus ?? null,
        'location.address': values.address ?? null,
        updatedAt: serverTimestamp(),
      });
      if (values.displayName !== firebaseUser.displayName) {
        await updateAuthProfile(firebaseUser, { displayName: values.displayName });
      }
      toast.success('Profile saved.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <section className="mx-auto max-w-2xl px-6 py-8">
      <h1 className="font-display text-3xl font-bold">Profile</h1>
      <InstitutionBadge
        institution={userDoc.studentVerification?.institution}
        className="mt-2"
      />
      <p className="mt-2 text-makhe-muted">Tell creators a bit about yourself.</p>

      <Card className="mt-6 flex items-center gap-4">
        <div className="relative h-20 w-20 overflow-hidden rounded-full bg-makhe-line/50">
          {photoURL ? (
            <img src={photoURL} alt="Your photo" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-makhe-muted"><User size={32} /></div>
          )}
        </div>
        <div>
          <label className="cursor-pointer">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-makhe-ink px-4 py-1.5 text-[11px] font-medium uppercase tracking-widest">
              {uploading ? (
                <>
                  <Loader2 size={13} className="animate-spin" /> Uploading…
                </>
              ) : (
                'Change photo'
              )}
            </span>
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => e.target.files?.[0] && onPhotoChange(e.target.files[0])}
            />
          </label>
          <p className="mt-1 text-xs text-makhe-muted/80">JPG/PNG, up to 5 MB.</p>
        </div>
      </Card>

      {userDoc.roles?.seller && (
        <ProfileReelEditor uid={firebaseUser.uid} videos={userDoc.sellerProfile?.galleryVideos} />
      )}

      <div className="mt-4 space-y-2">
        {userDoc.roles?.seller ? (
          <Link
            to="/seller/dashboard"
            className="flex items-center justify-between rounded-lg border border-makhe-line bg-makhe-paper px-4 py-3.5 transition-shadow hover:shadow-lift"
          >
            <div>
              <p className="font-display text-lg italic text-makhe-primary">Studio</p>
              <p className="text-xs text-makhe-muted">Services, bookings, earnings</p>
            </div>
            <ArrowRight size={16} className="text-makhe-muted" />
          </Link>
        ) : (
          <Link
            to="/seller/onboarding"
            className="flex items-center justify-between rounded-lg border border-makhe-line bg-makhe-paper px-4 py-3.5 transition-shadow hover:shadow-lift"
          >
            <div>
              <p className="font-display text-lg italic text-makhe-primary">Set up your studio</p>
              <p className="text-xs text-makhe-muted">Start selling on Makhe</p>
            </div>
            <ArrowRight size={16} className="text-makhe-muted" />
          </Link>
        )}
        {userDoc.roles?.admin && (
          <Link
            to="/admin"
            className="flex items-center justify-between rounded-lg border border-makhe-line bg-makhe-paper px-4 py-3.5 transition-shadow hover:shadow-lift"
          >
            <div>
              <p className="font-display text-lg text-makhe-forest">Admin</p>
              <p className="text-xs text-makhe-muted">Users, sellers, economics</p>
            </div>
            <ArrowRight size={16} className="text-makhe-muted" />
          </Link>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
        <Input
          label="Full name"
          {...register('displayName')}
          error={errors.displayName?.message}
          hint={
            isLikelyStudentNumberLocalPart(userDoc.displayName)
              ? 'Your student number is showing on listings — add your real name and surname here.'
              : 'Name and surname, as on your student card.'
          }
        />
        <Textarea label="Bio" rows={3} {...register('bio')} hint={`Up to ${BIO_MAX_CHARS} characters.`} />
        <Input
          label="Phone (required before booking)"
          type="tel"
          hint="Shared with your counterparty only after a booking is accepted."
          {...register('phoneNumber')}
        />
        <Select
          label="Gender"
          {...register('gender')}
          options={[
            { value: '', label: 'Prefer not to say' },
            { value: 'female', label: 'Female' },
            { value: 'male', label: 'Male' },
            { value: 'non_binary', label: 'Non-binary' },
            { value: 'prefer_not_to_say', label: 'Prefer not to say' },
          ]}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Campus" placeholder="Wits Braamfontein" {...register('campus')} />
          <Input label="Residence" placeholder="Knockando" {...register('residence')} />
        </div>
        <Input label="Address (optional)" {...register('address')} />

        <Button type="submit" loading={isSubmitting} className="w-full">
          Save
        </Button>
      </form>

      <Card className="mt-8">
        <h2 className="font-display text-lg">Refer a friend</h2>
        <p className="mt-1 text-sm text-makhe-ink/70">
          Share your link. When someone joins via your link and completes their first booking,
          you both get R50 credit.
        </p>
        <code className="mt-3 block break-all rounded-xl bg-makhe-paper p-3 text-xs">
          {window.location.origin}/auth/sign-up?ref={firebaseUser.uid}
        </code>
        <button
          onClick={() => {
            navigator.clipboard.writeText(
              `${window.location.origin}/auth/sign-up?ref=${firebaseUser.uid}`,
            );
            toast.success('Copied');
          }}
          className="mt-2 text-sm text-makhe-primary"
        >
          Copy link
        </button>
      </Card>

      <div className="mt-8 space-y-1 text-sm">
        <Link to="/profile/verify-student" className="flex items-center gap-2 py-2 text-makhe-primary">
          {userDoc.studentVerification?.status === 'verified'
            ? <><CheckCircle size={15} /> Student verified — view details</>
            : <>Verify student status <ArrowRight size={14} /></>}
        </Link>
        <Link to="/profile/campus-control" className="flex items-center gap-2 py-2 text-makhe-ink/70">
          Campus Control <ArrowRight size={14} />
        </Link>
        <Link to="/requests" className="flex items-center gap-2 py-2 text-makhe-ink/70">
          My requests <ArrowRight size={14} />
        </Link>
        <Link to="/favourites" className="flex items-center gap-2 py-2 text-makhe-ink/70">
          Saved creators <ArrowRight size={14} />
        </Link>
        <Link to="/settings/notifications" className="flex items-center gap-2 py-2 text-makhe-ink/70">
          Notifications <ArrowRight size={14} />
        </Link>
        <Link to="/settings/account" className="flex items-center gap-2 py-2 text-makhe-ink/70">
          Account settings <ArrowRight size={14} />
        </Link>
        <button
          onClick={() => void signOutUser()}
          className="py-2 text-makhe-muted/80 hover:text-makhe-ink"
        >
          Sign out
        </button>
      </div>
    </section>
  );
}
